import {
  removeBasketItems,
  addWishlistItemBySKU,
  removeWishlistItemBySKU,
  changeQuantity
} from "lib/api";
import type { ItemType } from "@shared/graphql.types";
import type { ProductListVariants } from "components/BasketLists/ProductList/ProductList";
import { useATBConfirmationContext } from "basket_and_checkout/shared/components/ATBConfirmation/context/ATBConfirmationContext";

export const locationTypes = {
  basket: "BASKET_CROSSSELL_CAROUSEL",
  atbConfirmation: "ATB_CONFIRMATION",
  default: "Page",
  membershipToggleFlyout: "membership_toggle_checkout_flyout",
  membershipToggleAlert: "membership_toggle_alert",
  promoVisibilityMembershipFlyout: "promo_visibility_membership_flyout",
  membershipToggleCard: "MEMBERSHIP_TOGGLE_CARD"
};

type OnAddRemoveWishlist = (
  item: ItemType,
  isWishListed: boolean
) => Promise<void>;

export const useAddRemoveWishlist = (): {
  onAddRemoveWishlist: OnAddRemoveWishlist;
} => {
  const onAddRemoveWishlist = async (item: ItemType, isWishListed: boolean) => {
    if (!item.variant) return;

    try {
      if (isWishListed) {
        await removeWishlistItemBySKU(item.variant.sku);
      } else {
        await addWishlistItemBySKU(item.variant.sku);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return { onAddRemoveWishlist };
};

type OnRemoveBasketItem = (item: ItemType) => Promise<void>;

export const useOnRemoveBasketItem = (
  cardVariant?: ProductListVariants
): {
  onRemoveBasketItem: OnRemoveBasketItem;
} => {
  const onRemoveBasketItem = async (item: ItemType) => {
    try {
      await removeBasketItems([item], locationTypes[cardVariant ?? "default"]);
    } catch (err) {
      console.error(err);
    }
  };

  return { onRemoveBasketItem };
};

type OnChangeQuantity = (item: ItemType, value: number) => Promise<void>;

export const useOnChangeQuantity = (
  cardVariant?: ProductListVariants
): {
  onChangeQuantity: OnChangeQuantity;
} => {
  const { setIsATBLoading } = useATBConfirmationContext();

  const disableGlobalLoader = cardVariant === "atbConfirmation";

  const onChangeQuantity = async (item: ItemType, value: number) => {
    try {
      if (disableGlobalLoader) {
        setIsATBLoading(true);
      }
      await changeQuantity(
        item.id,
        value,
        locationTypes[cardVariant ?? "default"],
        {
          runInBackground: disableGlobalLoader
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsATBLoading(false);
    }
  };

  return { onChangeQuantity };
};
